import Icon from '@/componentsDS/Icon/Icon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

const Checkbox = ({
	id,
	checked = false,
	indeterminate = false,
	onChange,
	name,
	label,
	disabled = false,
	addClass,
	backgroundColor,
	checkIcon = (
		<Icon
			name="check"
			size="small"
			style="outlined"
			color="#fff"
		/>
	),
	...inputProps
}) => {
	const checkboxRef = useRef(null);

	useEffect(() => {
		if (checkboxRef.current) {
			checkboxRef.current.indeterminate = indeterminate;
		}
	}, [indeterminate]);

	const checkboxClass = classNames('DS__check-box', addClass, {
		'DS__check-box--checked': checked,
		'DS__check-box--disabled': disabled,
		'DS__check-box--indeterminate': indeterminate
	});

	const uniqId = id ?? name ?? 'checkbox';

	const handleChange = (e) => {
		if (disabled) {
			return; // Prevent the onChange if disabled is true
		}
		onChange(e.target.checked);
	};

	return (
		<label className={checkboxClass} htmlFor={uniqId} data-testid={uniqId} onClick={(e) => e.stopPropagation()}>
			<input
				id={uniqId}
				ref={checkboxRef}
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={handleChange}
				{...inputProps}
			/>

			<div
				data-testid={`${uniqId}-custom`}
				className="DS__check-box__box"
				{...(backgroundColor &&
					(checked || indeterminate) && {
						style: {
							backgroundColor: backgroundColor,
							borderColor: backgroundColor
						}
					})}
			>
				{checked && checkIcon}

				{indeterminate && (
					<Icon
						name="minus"
						size="small"
						style="outlined"
						color="#fff"
					/>
				)}
			</div>

			{label && <span className="DS__check-box__label">{label}</span>}
		</label>
	);
};

Checkbox.propTypes = {
	id: PropTypes.string, // The ID of the checkbox, useful for accessibility and testing
	checked: PropTypes.bool, // Whether the checkbox is checked or not
	indeterminate: PropTypes.bool, // Whether the checkbox is in an indeterminate state
	onChange: PropTypes.func.isRequired, // Callback when the checkbox state changes
	name: PropTypes.string, // Name attribute for the checkbox input
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]), // Label to display next to the checkbox
	disabled: PropTypes.bool, // Whether the checkbox is disabled
	addClass: PropTypes.string, // Additional class(es) to apply to the checkbox
	backgroundColor: PropTypes.string, // Background color for the checkbox when checked or indeterminate
	checkIcon: PropTypes.node, // Custom icon to display when checked
	inputProps: PropTypes.object // Additional input attributes
};

// Add default props
Checkbox.defaultProps = {
	checked: false,
	indeterminate: false,
	disabled: false,
	checkIcon: (
		<Icon
			name="check"
			size="small"
			style="outlined"
			color="#fff"
		/>
	)
};

export default Checkbox;
